import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Stack,
    Text,
    IconButton,
    TooltipHost
} from '@fluentui/react';
import {
    Dropdown,
    Option,
    makeStyles
} from '@fluentui/react-components';
import './DashboardFilters.css';

const useStyles = makeStyles({
    dropdown: {
        minWidth: '250px'
    }
});

/**
 * DashboardFilters - Dynamic filters component for analytics dashboard
 * 
 * Loads filter options from Redux (flows, queues, agents)
 * and allows user to select values to filter data.
 * 
 * @param {string} tenantId - Tenant ID
 * @param {string} scope - Data scope (e.g., 'default')
 * @param {function} onFilterChange - Callback when filters change
 * @param {object} initialFilters - Initial filter values to restore state
 */
const DashboardFilters = ({ tenantId, scope, onFilterChange, initialFilters = {} }) => {
    const styles = useStyles();
    const { t } = useTranslation();

    // Get data from Redux
    const callFlows = useSelector(state => state.reducer.CALL_FLOWS);
    const queues = useSelector(state => state.reducer.QUEUES);
    const agents = useSelector(state => state.reducer.AGENTS);

    // State for selected values - store only keys
    const [selectedFlows, setSelectedFlows] = useState([]);
    const [selectedQueues, setSelectedQueues] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);

    // Track if initial restore has been done
    const isInitialMount = useRef(true);

    // Helper function to convert object or array to array
    const toArray = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        // If it's an object, convert to array of values
        return Object.values(data);
    };

    // Build dropdown options from Redux data - MEMOIZED to prevent recreation
    const flowOptions = useMemo(() =>
        toArray(callFlows).map(flow => ({
            key: String(flow.flowId || flow.id || flow.flowKey),
            text: flow.flowName || flow.displayName || flow.name || flow.id
        }))
        , [callFlows]);

    const queueOptions = useMemo(() =>
        toArray(queues).map(queue => ({
            key: String(queue.queueName || queue.displayName || queue.name || queue.id),
            text: queue.queueName || queue.displayName || queue.name || queue.id
        }))
        , [queues]);

    const agentOptions = useMemo(() =>
        toArray(agents).map(agent => ({
            key: String(agent.id || agent.agentKey || agent.odataId),
            text: agent.displayName || agent.name || agent.id
        }))
        , [agents]);

    /**
     * Restore state from initialFilters ONLY on initial mount
     */
    useEffect(() => {
        if (!isInitialMount.current) return;
        isInitialMount.current = false;

        if (initialFilters.flowKey && Array.isArray(initialFilters.flowKey)) {
            setSelectedFlows(initialFilters.flowKey);
        }
        if (initialFilters.queueKey && Array.isArray(initialFilters.queueKey)) {
            setSelectedQueues(initialFilters.queueKey);
        }
        if (initialFilters.agentKey && Array.isArray(initialFilters.agentKey)) {
            setSelectedAgents(initialFilters.agentKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Notify parent when filters change
     */
    useEffect(() => {
        const filters = {};

        if (selectedFlows?.length > 0) {
            filters.flowKey = selectedFlows;
        }
        if (selectedQueues?.length > 0) {
            filters.queueKey = selectedQueues;
        }
        if (selectedAgents?.length > 0) {
            filters.agentKey = selectedAgents;
        }

        onFilterChange(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFlows, selectedQueues, selectedAgents]);

    /**
     * Reset all filters
     */
    const handleClearFilters = () => {
        setSelectedFlows([]);
        setSelectedQueues([]);
        setSelectedAgents([]);
    };

    const hasActiveFilters = (selectedFlows?.length > 0) || (selectedQueues?.length > 0) || (selectedAgents?.length > 0);

    return (
        <div style={{ display: 'contents' }}>
            {/* Flow Filter - Multi-select */}
            <Dropdown
                className={styles.dropdown}
                multiselect={true}
                placeholder={t('FLOW_FILTER_PLACEHOLDER')}
                value=""
                selectedOptions={selectedFlows}
                onOptionSelect={(e, data) => {
                    setSelectedFlows(data.selectedOptions);
                }}
                style={{ minWidth: '180px', width: '180px', height: '32px' }}
            >
                {flowOptions.map((option) => (
                    <Option key={option.key} value={option.key}>
                        {option.text}
                    </Option>
                ))}
            </Dropdown>

            {/* Queue Filter - Multi-select */}
            <Dropdown
                className={styles.dropdown}
                multiselect={true}
                placeholder={t('QUEUE_FILTER_PLACEHOLDER')}
                value=""
                selectedOptions={selectedQueues}
                onOptionSelect={(e, data) => {
                    setSelectedQueues(data.selectedOptions);
                }}
                style={{ minWidth: '180px', width: '180px', height: '32px' }}
            >
                {queueOptions.map((option) => (
                    <Option key={option.key} value={option.key}>
                        {option.text}
                    </Option>
                ))}
            </Dropdown>

            {/* Agent Filter - Multi-select */}
            <Dropdown
                className={styles.dropdown}
                multiselect={true}
                placeholder={t('AGENT_FILTER_PLACEHOLDER')}
                value=""
                selectedOptions={selectedAgents}
                onOptionSelect={(e, data) => {
                    setSelectedAgents(data.selectedOptions);
                }}
                style={{ minWidth: '180px', width: '180px', height: '32px' }}
            >
                {agentOptions.map((option) => (
                    <Option key={option.key} value={option.key}>
                        {option.text}
                    </Option>
                ))}
            </Dropdown>

            {/* Display active filters - nouvelle ligne en dessous */}
            {hasActiveFilters && (
                <div style={{ width: '100%', marginTop: '10px' }}>
                    <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
                        <Text variant="small" styles={{ root: { color: '#605e5c', paddingTop: 5 } }}>
                            {t('ACTIVE_FILTERS')}
                        </Text>
                        {selectedFlows.map(flowKey => {
                            const flow = flowOptions.find(f => f.key === flowKey);
                            return flow ? (
                                <Stack key={flowKey} horizontal tokens={{ childrenGap: 5 }} className="filter-tag filter-tag-flow">
                                    <Text variant="small" styles={{ root: { color: 'white' } }}>
                                        {t('FLOW')}: {flow.text}
                                    </Text>
                                    <button
                                        className="filter-tag-remove"
                                        onClick={() => setSelectedFlows(prev => prev.filter(k => k !== flowKey))}
                                        title="Supprimer ce filtre"
                                    >
                                        ×
                                    </button>
                                </Stack>
                            ) : null;
                        })}
                        {selectedQueues.map(queueKey => {
                            const queue = queueOptions.find(q => q.key === queueKey);
                            return queue ? (
                                <Stack key={queueKey} horizontal tokens={{ childrenGap: 5 }} className="filter-tag filter-tag-queue">
                                    <Text variant="small" styles={{ root: { color: 'white' } }}>
                                        {t('QUEUE')}: {queue.text}
                                    </Text>
                                    <button
                                        className="filter-tag-remove"
                                        onClick={() => setSelectedQueues(prev => prev.filter(k => k !== queueKey))}
                                        title="Supprimer ce filtre"
                                    >
                                        ×
                                    </button>
                                </Stack>
                            ) : null;
                        })}
                        {selectedAgents.map(agentKey => {
                            const agent = agentOptions.find(a => a.key === agentKey);
                            return agent ? (
                                <Stack key={agentKey} horizontal tokens={{ childrenGap: 5 }} className="filter-tag filter-tag-agent">
                                    <Text variant="small" styles={{ root: { color: 'white' } }}>
                                        {t('AGENT')}: {agent.text}
                                    </Text>
                                    <button
                                        className="filter-tag-remove"
                                        onClick={() => setSelectedAgents(prev => prev.filter(k => k !== agentKey))}
                                        title="Supprimer ce filtre"
                                    >
                                        ×
                                    </button>
                                </Stack>
                            ) : null;
                        })}
                    </Stack>
                </div>
            )}
        </div>
    );
};

export default DashboardFilters;
